import { Link, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { History, Pencil } from 'lucide-react'
import { getLocations, getUser } from '../../api/master'
import ErrorBanner from '../../components/ErrorBanner'
import Spinner from '../../components/Spinner'
import { BackLink, DocHeader, StatusBadge } from '../../components/PageParts'
import { fmtDay } from '../../utils/format'
import { useSession } from '../../session'
import { ROLES, photoSrc, roleList, userActive } from './masterShared'

// A user's profile: who they are, what they can open and where they work.
// Everyone can see their own; admins see anyone's and can change it.
export default function UserDetail() {
  const { userId } = useParams()
  const { user: me, roles: myRoles = [] } = useSession()
  const admin = myRoles.includes('admin')
  const isMe = String(me?.user_id) === String(userId)
  const { data: user, isLoading, error } = useQuery({ queryKey: ['user', userId], queryFn: () => getUser(userId), retry: false })
  const { data: locations = [] } = useQuery({ queryKey: ['locations'], queryFn: getLocations })

  const back = admin ? <BackLink to="/master/users" label="Users" /> : <BackLink to="/" label="Overview" />
  if (isLoading) return <div className="ci-form-page">{back}<Spinner label="Loading profile…" /></div>
  if (error || !user) {
    const status = error?.response?.status
    return (
      <div className="ci-form-page">
        <DocHeader back={back} title="Profile" />
        <ErrorBanner message={status === 404 ? 'This user wasn’t found.' : status === 403 ? 'You can only see your own profile.' : 'Couldn’t load the profile.'} />
      </div>
    )
  }

  const name = user.full_name || user.username
  const roles = roleList(user.role)
  const pic = photoSrc(user.photo_url)
  const location = locations.find((l) => Number(l.location_id) === Number(user.location_id))?.name
  const row = (label, value) => (
    <div className="mf-detail-row"><dt>{label}</dt><dd className={value ? '' : 'ci-muted-cell'}>{value || 'Not given'}</dd></div>
  )

  return (
    <div className="ci-form-page">
      <DocHeader
        back={back}
        title={isMe ? 'Your profile' : name}
        badge={userActive(user) ? <StatusBadge label="Active" tone="good" /> : <StatusBadge label="Inactive" tone="muted" />}
        meta={`@${user.username}`}
        actions={admin && (
          <>
            <Link className="ci-btn ci-btn-ghost" to={`/utilities/audit?actor=${encodeURIComponent(user.username || '')}`}><History size={15} />Activity</Link>
            <Link className="ci-btn ci-btn-primary" to={`/master/users/${userId}/edit`}><Pencil size={15} />Edit</Link>
          </>
        )}
      />

      <div className="ci-form-meta-card mf-form">
        <div className="mf-person">
          <div className="mf-photo">{pic ? <img src={pic} alt="" /> : <span>{String(name || '?').slice(0, 1).toUpperCase()}</span>}</div>
          <dl className="mf-person-fields mf-detail">
            {row('Name', name)}
            {row('Email', user.email)}
            {row('Phone', user.phone)}
            {row('Address', user.address)}
            {row('Home location', location || (roles.includes('admin') ? 'All locations' : ''))}
            {row('Date employed', user.date_employed ? fmtDay(String(user.date_employed).slice(0, 10)) : '')}
            {row('National ID', user.national_id)}
          </dl>
        </div>
      </div>

      <div className="ci-form-meta-card mf-form">
        <div className="mf-card-title">Can open</div>
        <ul className="mf-role-list">
          {ROLES.filter((r) => roles.includes(r.value)).map((r) => (
            <li key={r.value}><strong>{r.label}</strong><span>{r.desc}</span></li>
          ))}
        </ul>
        {isMe && !admin && <p className="sum-muted">To change your details or access, ask an admin.</p>}
      </div>
    </div>
  )
}
